import {
  Box,
  Flex,
  Icon,
  Image,
  Stack,
  StackProps,
  Tooltip,
} from '@chakra-ui/react';
import {
  RotateLeft as MdRotateLeft,
  RotateRight as MdRotateRight,
} from '@material-ui/icons';
import {
  ProjectOrJobPictureId,
  PictureType,
} from '~/common/entities/Pictures/PictureTypes';
import { ArrowBackIcon, ArrowForwardIcon, DeleteIcon } from '@chakra-ui/icons';
import { usePictureService } from '~/common/services/PictureService';
import { PictureUploadDropArea } from './PictureUploadDropArea';
import React, { useEffect, useMemo, useState } from 'react';
import { DefaultAlertDialog } from '../defaultAlertDialog';
import useTranslate from '~/common/utils/translate';
import { DefaultSpinner } from '../DefaultSpinner';
import { Select } from '../Select';
import { Option } from '../Option';
import { Badge } from '../Badge';
import _ from 'lodash';

type PictureViewerProps = StackProps & {
  pictureIds: ProjectOrJobPictureId[];
  pictureType: PictureType;
  projectOrJobId: string;
  showInModal?: boolean;
  closeModal?: () => void;
  maxHeightPreview?: string;
  maxWidthPreview?: string;
  showDropArea?: boolean;
};

export const PictureViewer: React.FC<PictureViewerProps> = ({
  pictureIds: initPictureIds,
  pictureType,
  projectOrJobId,
  showInModal = true,
  closeModal = () => {},
  maxHeightPreview = '9vh',
  maxWidthPreview = '9vh',
  showDropArea = true,
  ...props
}: PictureViewerProps) => {
  const translate = useTranslate();
  const buttonSize = 10;
  const pictureService = usePictureService();
  const { rotatePicture } = pictureService;
  const [currentPictureIndex, setCurrentPictureIndex] = useState<number | null>(
    0,
  );
  const [openDelete, setOpenDelete] = useState(false);

  const toggleOpenDelete = () => {
    setOpenDelete(!openDelete);
  };

  useMemo(() => {
    setCurrentPictureIndex((initPictureIds?.length || 0) > 0 ? 0 : null);
  }, [initPictureIds]);

  const currentPictureId = useMemo(() => {
    return currentPictureIndex === null || !initPictureIds
      ? null
      : `${initPictureIds[currentPictureIndex]?.id}-${pictureType}`;
  }, [currentPictureIndex]);

  const increasePictureId = () => {
    setCurrentPictureIndex((current) =>
      initPictureIds.length === current! + 1 ? 0 : current! + 1,
    );
  };

  const decreasePictureId = () => {
    setCurrentPictureIndex((current) =>
      current! - 1 < 0 ? initPictureIds.length - 1 : current! - 1,
    );
  };

  useEffect(() => {
    if (currentPictureId)
      pictureService.loadPicture(currentPictureId, pictureType);
  }, [initPictureIds, currentPictureId]);

  const currentPicture = useMemo(
    () => _.get(pictureService.picturesData, currentPictureId!),
    [currentPictureId, pictureService.picturesData],
  );

  const rotatePictureToLeft = () => {
    rotatePicture(true, currentPictureId!, pictureType);
  };

  const rotatePictureToRight = () => {
    rotatePicture(false, currentPictureId!, pictureType);
  };

  const onDeleteModalAction = async () => {
    await pictureService.deletePicture(
      currentPictureId!,
      pictureType,
      projectOrJobId,
      (isLast) => {
        if (isLast) {
          closeModal();
        }
      },
    );
    toggleOpenDelete();
  };

  const moveElementWithinArray = (
    array: any[],
    indexWhichIsToMove: number,
    indexWhereToMove: number,
  ) => {
    if (indexWhereToMove >= array.length) {
      let k = indexWhereToMove - array.length + 1;
      while (k--) {
        array.push(undefined);
      }
    }
    array = array.splice(
      indexWhereToMove,
      0,
      array.splice(indexWhichIsToMove, 1)[0],
    );
  };

  const updateOrderNumber = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newOrderNumberString = event.currentTarget.value;
    const newOrderNumber = parseInt(newOrderNumberString, 10) - 1;
    const updatePicturesLocallyOnSuccess = () => {
      moveElementWithinArray(
        initPictureIds,
        currentPictureIndex,
        newOrderNumber,
      );
      setCurrentPictureIndex(newOrderNumber);
    };

    pictureService.updateOrderNumber(
      currentPictureId,
      currentPicture.rotation,
      newOrderNumber,
      updatePicturesLocallyOnSuccess,
    );
  };

  const getMargin = () => {
    if (props.margin) {
      return props.margin;
    } else if (props.m) {
      return props.m;
    } else {
      return 'auto';
    }
  };

  if (!currentPictureId && showDropArea)
    return (
      <PictureUploadDropArea
        pictureType={pictureType}
        projectOrJobId={projectOrJobId}
        width="200px"
        mt={0}
        height="50%"
      />
    );

  if (!currentPicture && showDropArea)
    return <DefaultSpinner h={showInModal ? '60vh' : '100%'} />;

  if (!currentPicture && !showDropArea) {
    return null;
  }

  return (
    <Stack w="fit-content" margin={getMargin()} {...props}>
      <Flex align="center">
        {showInModal ? (
          <ArrowBackIcon
            aria-label={translate(
              'pictureViewer.showPictures.lastPicture.ariaLabel',
            )}
            onClick={decreasePictureId}
            backgroundColor="white"
            color="black"
            boxSize={buttonSize}
            zIndex={2}
            cursor="pointer"
          />
        ) : null}
        <Box>
          <Image
            margin="auto"
            src={currentPicture.picture}
            maxHeight={showInModal ? '80vh' : maxHeightPreview}
            maxWidth={showInModal ? '80%' : maxWidthPreview}
            transform={'rotate(' + currentPicture.rotation + 'deg)'}
          />
        </Box>
        {!showInModal ? (
          <Badge fontSize="xl">{initPictureIds.length}</Badge>
        ) : null}
        {showInModal ? (
          <ArrowForwardIcon
            aria-label={translate(
              'pictureViewer.showPictures.nextPicture.ariaLabel',
            )}
            onClick={increasePictureId}
            backgroundColor="white"
            color="black"
            boxSize={buttonSize}
            zIndex={2}
            cursor="pointer"
          />
        ) : null}
      </Flex>
      {showInModal ? (
        <Flex justify="center">
          <Flex mr={4}>
            <PictureUploadDropArea
              pictureType={pictureType}
              projectOrJobId={projectOrJobId}
              width="100%"
            />
          </Flex>
          <Flex mr={2}>
            {pictureType === PictureType.JOB ? (
              <Select
                label="pictureViewer.jobPicture.orderNumber"
                value={currentPictureIndex + 1}
                onChange={updateOrderNumber}
                labelAsText
              >
                {initPictureIds.map((id, index) => (
                  <Option>{index + 1}</Option>
                ))}
              </Select>
            ) : null}
          </Flex>
          <Flex>
            <Tooltip
              aria-label={translate('pictureViewer.rotateLeft.tooltip.label')}
            >
              <Icon
                aria-label={translate('pictureViewer.rotateLeft.tooltip.label')}
                as={MdRotateLeft}
                onClick={rotatePictureToLeft}
                backgroundColor="white"
                color="black"
                boxSize={buttonSize}
                fontSize={30}
                zIndex={2}
                cursor="pointer"
              />
            </Tooltip>
            <Tooltip
              aria-label={translate(
                'pictureViewer.deleteProject.tooltip.label',
              )}
            >
              <DeleteIcon
                aria-label={translate(
                  'pictureViewer.deleteProject.tooltip.label',
                )}
                onClick={toggleOpenDelete}
                backgroundColor="white"
                color="black"
                boxSize={buttonSize}
                zIndex={2}
                cursor="pointer"
              />
            </Tooltip>
            <DefaultAlertDialog
              dialogHeaderText="departmentTile.departmentDelete.dialogHeaderText"
              dialogBodyText="departmentTile.departmentDelete.dialogBodyText"
              isOpen={openDelete}
              onConfirmedAction={onDeleteModalAction}
              onCancel={toggleOpenDelete}
            />
            <Tooltip
              aria-label={translate('pictureViewer.rotateRight.tooltip.label')}
            >
              <Icon
                aria-label={translate(
                  'pictureViewer.rotateRight.tooltip.label',
                )}
                as={MdRotateRight}
                onClick={rotatePictureToRight}
                backgroundColor="white"
                color="black"
                boxSize={buttonSize}
                zIndex={2}
                cursor="pointer"
              />
            </Tooltip>
          </Flex>
        </Flex>
      ) : null}
    </Stack>
  );
};
